import { formatHexDump, type PacketSummary } from './pcapParse';

export const PCAP_STREAM_MAX_BYTES = 256 * 1024;

export type StreamDirection = 'a' | 'b';

export interface StreamSegment {
  direction: StreamDirection;
  bytes: Uint8Array;
  packetIndex: number;
  endpointLabel: string;
}

export interface StreamConversation {
  streamId: number;
  endpointA: string;
  endpointB: string;
  segments: StreamSegment[];
  truncated: boolean;
}

interface PayloadPacket {
  packet: PacketSummary;
  payload: Uint8Array;
  endpoint: string;
  direction: StreamDirection;
}

export function buildStreamConversation(
  packets: PacketSummary[],
  streamId: number,
  maxBytes = PCAP_STREAM_MAX_BYTES,
): StreamConversation {
  const streamPackets = packets
    .filter((packet) => packet.streamId === streamId)
    .slice()
    .sort((a, b) => a.timestampMs - b.timestampMs || a.index - b.index);

  const withPayload: PayloadPacket[] = [];
  let endpointA = '';
  let endpointB = '';

  for (const packet of streamPackets) {
    if (packet.payloadOffset == null || packet.payloadLength == null || packet.payloadLength <= 0) {
      continue;
    }
    if (packet.srcPort == null || packet.dstPort == null) continue;

    const endpoint = `${packet.src}:${packet.srcPort}`;
    const opposite = `${packet.dst}:${packet.dstPort}`;
    if (!endpointA) {
      endpointA = endpoint;
      endpointB = opposite;
    }

    const direction: StreamDirection = endpoint === endpointA ? 'a' : 'b';
    if (direction === 'b' && !endpointB) {
      endpointB = endpoint;
    }

    const payload = packet.bytes.subarray(
      packet.payloadOffset,
      packet.payloadOffset + packet.payloadLength,
    );
    withPayload.push({ packet, payload, endpoint, direction });
  }

  if (!endpointA && streamPackets[0]) {
    const first = streamPackets[0];
    if (first.srcPort != null) {
      endpointA = `${first.src}:${first.srcPort}`;
    }
    if (first.dstPort != null) {
      endpointB = `${first.dst}:${first.dstPort}`;
    }
  }

  const runs = groupDirectionRuns(withPayload);
  const segments: StreamSegment[] = [];
  let usedBytes = 0;
  let truncated = false;

  for (const run of runs) {
    if (usedBytes >= maxBytes) {
      truncated = true;
      break;
    }

    const isTcp = run[0]?.packet.protocol === 'TCP';
    const merged = isTcp ? mergeTcpRun(run) : mergeUdpRun(run);
    if (merged.bytes.length === 0) continue;

    const remaining = maxBytes - usedBytes;
    let bytes = merged.bytes;
    if (bytes.length > remaining) {
      bytes = bytes.subarray(0, remaining);
      truncated = true;
    }

    segments.push({
      direction: run[0].direction,
      bytes,
      packetIndex: merged.packetIndex,
      endpointLabel: run[0].endpoint,
    });
    usedBytes += bytes.length;

    if (truncated) break;
  }

  return {
    streamId,
    endpointA,
    endpointB,
    segments,
    truncated,
  };
}

export function formatStreamText(bytes: Uint8Array): string {
  const decoded = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
  let result = '';
  for (const ch of decoded) {
    const code = ch.codePointAt(0) ?? 0;
    if (ch === '\n' || ch === '\r' || ch === '\t') {
      result += ch;
    } else if (code < 32 || code === 0x7f) {
      result += '.';
    } else {
      result += ch;
    }
  }
  return result;
}

export function formatStreamSegmentHex(bytes: Uint8Array): string {
  return formatHexDump(bytes);
}

function groupDirectionRuns(packets: PayloadPacket[]): PayloadPacket[][] {
  const runs: PayloadPacket[][] = [];
  let current: PayloadPacket[] = [];

  for (const item of packets) {
    if (current.length === 0 || current[0].direction === item.direction) {
      current.push(item);
    } else {
      runs.push(current);
      current = [item];
    }
  }
  if (current.length > 0) runs.push(current);
  return runs;
}

function mergeUdpRun(run: PayloadPacket[]): { bytes: Uint8Array; packetIndex: number } {
  const total = run.reduce((sum, item) => sum + item.payload.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const item of run) {
    out.set(item.payload, offset);
    offset += item.payload.length;
  }
  return { bytes: out, packetIndex: run[0].packet.index };
}

function mergeTcpRun(run: PayloadPacket[]): { bytes: Uint8Array; packetIndex: number } {
  const ordered = run
    .slice()
    .sort((a, b) => {
      const seqA = a.packet.tcpSeq ?? 0;
      const seqB = b.packet.tcpSeq ?? 0;
      return seqA - seqB || a.packet.index - b.packet.index;
    });

  const chunks: Uint8Array[] = [];
  let expectedSeq: number | null = null;
  let packetIndex = ordered[0]?.packet.index ?? 0;

  for (const item of ordered) {
    const seq = item.packet.tcpSeq;
    if (seq == null) {
      chunks.push(item.payload);
      continue;
    }

    if (expectedSeq == null) {
      chunks.push(item.payload);
      expectedSeq = (seq + item.payload.length) >>> 0;
      packetIndex = item.packet.index;
      continue;
    }

    // Retransmission / overlap already covered
    if (unsignedLt(seq, expectedSeq)) {
      const alreadyCovered = unsignedDiff(expectedSeq, seq);
      if (alreadyCovered >= item.payload.length) {
        continue;
      }
      const remaining = item.payload.subarray(alreadyCovered);
      chunks.push(remaining);
      expectedSeq = (expectedSeq + remaining.length) >>> 0;
      continue;
    }

    // Exact next segment
    if (seq === expectedSeq) {
      chunks.push(item.payload);
      expectedSeq = (expectedSeq + item.payload.length) >>> 0;
      continue;
    }

    // Gap: stop merging further segments in this run (plan: stop on gaps)
    break;
  }

  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return { bytes: out, packetIndex };
}

/** Unsigned 32-bit comparison (TCP sequence space). */
function unsignedLt(a: number, b: number): boolean {
  return ((a - b) >>> 0) > 0x80000000;
}

function unsignedDiff(a: number, b: number): number {
  return (a - b) >>> 0;
}
